import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { Eye, Plus, Trash2 } from "lucide-react";
import api from "../../../services/apiService";
import DataTable, {
  createActionsColumn,
  DataTableButton,
} from "../../../components/data-table";
import AlertNotification from "../../../components/notification";
import DeleteModal from "../../../components/models/deleteModal.jsx";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

const STATUS_BADGE = {
  draft: "bg-gray-100 border-gray-200 text-gray-500",
  processed: "bg-blue-50 border-blue-200 text-blue-700",
  approved: "bg-purple-50 border-purple-200 text-purple-700",
  paid: "bg-green-50 border-green-200 text-green-700",
  cancelled: "bg-red-50 border-red-200 text-red-700",
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function CreateModal({ show, onClose, onSubmit, isSubmitting, error }) {
  const [form, setForm] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    title: "",
  });
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 dark:bg-black/60 p-4">
      <button type="button" onClick={onClose} className="absolute inset-0" />
      <div className="relative w-full max-w-sm rounded-2xl border border-app-primary bg-app-card shadow-2xl dark:shadow-black/50">
        <div className="flex items-center justify-between border-b border-app-primary px-6 py-4">
          <h3 className="text-lg font-semibold text-app-heading">
            Create Payroll
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-app-muted transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
          >
            <FaTimes />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(form);
          }}
          className="px-6 py-5 space-y-4"
        >
          {error && (
            <p className="rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">
              {error}
            </p>
          )}
          <div>
            <label
              htmlFor="pr-month"
              className="mb-1 block text-sm font-medium text-app-secondary"
            >
              Month
            </label>
            <select
              id="pr-month"
              value={form.month}
              onChange={(e) =>
                setForm((p) => ({ ...p, month: Number(e.target.value) }))
              }
              className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
            >
              {MONTHS.map((m, i) => (
                <option key={i} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="pr-year"
              className="mb-1 block text-sm font-medium text-app-secondary"
            >
              Year
            </label>
            <input
              id="pr-year"
              type="number"
              value={form.year}
              onChange={(e) =>
                setForm((p) => ({ ...p, year: Number(e.target.value) }))
              }
              className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label
              htmlFor="pr-title"
              className="mb-1 block text-sm font-medium text-app-secondary"
            >
              Title (optional)
            </label>
            <input
              id="pr-title"
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              placeholder="e.g. June 2026 Payroll"
              className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
            />
          </div>
          <div className="flex justify-end gap-3 border-t border-app-primary pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-app-primary px-5 py-2 text-sm font-semibold text-app-secondary transition hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg border border-[#2470B7] bg-[#2470B7] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500"
            >
              {isSubmitting ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

function HrPayroll() {
  const navigate = useNavigate();
  const [payrolls, setPayrolls] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchPayrolls = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/hr/payrolls", { headers: authHeaders() });
      setPayrolls(Array.isArray(res.data) ? res.data : []);
    } catch {
      setNotification({ text: "Failed to load payrolls.", status: false });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayrolls();
  }, [fetchPayrolls]);

  const handleCreate = async (form) => {
    setIsCreating(true);
    setCreateError("");
    try {
      const res = await api.post("/hr/payrolls", form, {
        headers: authHeaders(),
      });
      setShowCreate(false);
      navigate(`/hr/payroll/${res.data.id}`);
    } catch (err) {
      setCreateError(err?.response?.data?.message || "Failed to create.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/hr/payrolls/${deleteTarget.id}`, {
        headers: authHeaders(),
      });
      setNotification({ text: "Payroll deleted.", status: true });
      setDeleteTarget(null);
      fetchPayrolls();
    } catch {
      setNotification({ text: "Failed to delete payroll.", status: false });
    } finally {
      setIsDeleting(false);
    }
  };

  const period = (p) => `${MONTHS[p.month - 1]} ${p.year}`;

  const columns = [
    {
      key: "title",
      label: "Title",
      sortable: true,
      searchable: true,
      accessor: (p) => p.title || period(p),
      render: (v) => <span className="font-medium text-app-secondary">{v}</span>,
    },
    {
      key: "period",
      label: "Period",
      sortable: true,
      searchable: true,
      accessor: period,
      sortValue: (p) => p.year * 12 + p.month,
      render: (v) => <span className="text-app-secondary">{v}</span>,
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      searchable: true,
      render: (v) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[v] || ""}`}
        >
          {v}
        </span>
      ),
    },
    {
      key: "totalGrossSalary",
      label: "Gross",
      sortable: true,
      align: "right",
      sortValue: (p) => Number(p.totalGrossSalary) || 0,
      render: (v) => <span className="font-mono text-xs">{fmt(v)}</span>,
    },
    {
      key: "totalDeductions",
      label: "Deductions",
      sortable: true,
      align: "right",
      sortValue: (p) => Number(p.totalDeductions) || 0,
      render: (v) => <span className="font-mono text-xs">{fmt(v)}</span>,
    },
    {
      key: "totalNetSalary",
      label: "Net",
      sortable: true,
      align: "right",
      sortValue: (p) => Number(p.totalNetSalary) || 0,
      render: (v) => (
        <span className="font-mono text-xs font-semibold text-app-primary">
          {fmt(v)}
        </span>
      ),
    },
    createActionsColumn(
      (p) => [
        {
          label: "View details",
          icon: Eye,
          onSelect: () => navigate(`/hr/payroll/${p.id}`),
        },
        {
          label: "Delete",
          icon: Trash2,
          tone: "danger",
          onSelect: () => setDeleteTarget(p),
        },
      ],
      { getRowLabel: (p) => p.title || period(p) },
    ),
  ];

  return (
    <div className="space-y-5">
      <p className="text-sm text-app-muted">Manage monthly payroll batches.</p>

      <DataTable
        ariaLabel="Payroll batches"
        columns={columns}
        data={payrolls}
        loading={isLoading}
        searchable
        searchPlaceholder="Search title, period or status"
        toolbarRight={
          <DataTableButton
            variant="primary"
            icon={Plus}
            onClick={() => {
              setCreateError("");
              setShowCreate(true);
            }}
          >
            Create Payroll
          </DataTableButton>
        }
        onRowClick={(p) => navigate(`/hr/payroll/${p.id}`)}
        pagination
        defaultPageSize={10}
        labels={{ empty: "No payrolls yet." }}
      />

      {notification && (
        <AlertNotification
          text={notification.text}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
      <CreateModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
        isSubmitting={isCreating}
        error={createError}
      />
      <DeleteModal
        show={Boolean(deleteTarget)}
        handleClose={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
        handleConfirm={handleDelete}
        message={`Delete payroll "${deleteTarget?.title || ""}"?`}
        loading={isDeleting}
      />
    </div>
  );
}

export default HrPayroll;
